import { safeLocalStorage } from "../utils/safeStorage";
import {
  resolveFreshFbc,
  writeFbcCookie,
} from "./metaCapiParameters";
import {
  appendPaidAttributionTouchpoint,
  buildPaidAttributionTouchpoint,
  resolvePaidAttribution,
} from "./paidAttribution";

const STORAGE_KEY = "linkedLivesAdAttribution";
const GOOGLE_CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"];
const META_CLICK_ID_KEYS = ["fbclid"];
const CLICK_ID_KEYS = [...GOOGLE_CLICK_ID_KEYS, ...META_CLICK_ID_KEYS];
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];
const MAX_VALUE_LENGTH = 180;
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

// Capture the document entry before client-side navigation changes the URL.
const browserEntry = {
  path: typeof window !== "undefined" ? window.location.pathname : "",
  referrer: typeof document !== "undefined" ? document.referrer : "",
};

export const readBrowserEntry = () => browserEntry;

const safeStorage = () => {
  if (typeof window === "undefined") return null;
  return safeLocalStorage;
};

const cleanValue = (value) =>
  String(value || "")
    .trim()
    .slice(0, MAX_VALUE_LENGTH);

const cleanClickIdValue = (value) => String(value || "").trim();

function getCookie(name) {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

function refreshFbcCookieFromUrl({ href, nowMs }) {
  const currentFbc = getCookie("_fbc");
  const fbc = resolveFreshFbc({
    eventSourceUrl: href,
    currentFbc,
    nowMs,
  });

  if (fbc && fbc !== currentFbc) {
    writeFbcCookie(fbc);
  }
}

export function readStoredAdAttribution(nowMs = Date.now()) {
  const storage = safeStorage();
  if (!storage) return null;

  try {
    const parsed = JSON.parse(storage.getItem(STORAGE_KEY) || "null");
    if (!parsed || typeof parsed !== "object") return null;
    const capturedAtMs = Number(parsed.capturedAtMs || 0);
    if (!Number.isFinite(capturedAtMs) || nowMs - capturedAtMs > MAX_AGE_MS) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (_error) {
    return null;
  }
}

export function captureAdAttributionFromUrl({
  href = typeof window !== "undefined" ? window.location.href : "",
  referrer = typeof document !== "undefined" ? document.referrer : "",
  nowMs = Date.now(),
} = {}) {
  const storage = safeStorage();
  if (!storage || !href) return null;

  let url;
  try {
    url = new URL(href);
  } catch (_error) {
    return readStoredAdAttribution(nowMs);
  }

  const clickIds = CLICK_ID_KEYS.reduce((acc, key) => {
    const value = cleanClickIdValue(url.searchParams.get(key));
    if (value) acc[key] = value;
    return acc;
  }, {});
  const utm = UTM_KEYS.reduce((acc, key) => {
    const value = cleanValue(url.searchParams.get(key));
    if (value) acc[key] = value;
    return acc;
  }, {});

  if (!Object.keys(clickIds).length && !Object.keys(utm).length) {
    return readStoredAdAttribution(nowMs);
  }

  if (clickIds.fbclid) {
    refreshFbcCookieFromUrl({ href, nowMs });
  }

  const previous = readStoredAdAttribution(nowMs) || {};
  const clickIdCapturedAtMs = {
    ...(previous.clickIdCapturedAtMs || {}),
  };
  Object.keys(clickIds).forEach((key) => {
    clickIdCapturedAtMs[key] = nowMs;
  });
  const nextTouchpoint = buildPaidAttributionTouchpoint({
    source: GOOGLE_CLICK_ID_KEYS.some((key) => clickIds[key])
      ? "google"
      : META_CLICK_ID_KEYS.some((key) => clickIds[key])
        ? "facebook"
        : cleanValue(utm.utm_source) || previous.source || "unknown",
    clickIds,
    clickIdCapturedAtMs,
    utm,
    landingPage: `${url.pathname}${url.search}`,
    referrer,
    capturedAtMs: nowMs,
  });
  const touchpoints = appendPaidAttributionTouchpoint({
    previousTouchpoints: previous.touchpoints || [],
    touchpoint: nextTouchpoint,
  });
  const paidAttribution = resolvePaidAttribution({
    storedAttribution: {
      ...previous,
      clickIds: {
        ...(previous.clickIds || {}),
        ...clickIds,
      },
      clickIdCapturedAtMs,
      utm: {
        ...(previous.utm || {}),
        ...utm,
      },
      touchpoints,
      capturedAtMs: nowMs,
    },
  });

  const payload = {
    ...previous,
    clickIds: {
      ...(previous.clickIds || {}),
      ...clickIds,
    },
    clickIdCapturedAtMs,
    utm: {
      ...(previous.utm || {}),
      ...utm,
    },
    landingPage: cleanValue(`${url.pathname}${url.search}`),
    referrer: cleanValue(referrer),
    capturedAtMs: nowMs,
    touchpoints,
    attributionModelVersion: paidAttribution.attributionModelVersion,
    acquisitionProvider: paidAttribution.acquisitionProvider || null,
    acquisitionSource: paidAttribution.acquisitionSource || null,
    conversionProvider: paidAttribution.conversionProvider || null,
    conversionSource: paidAttribution.conversionSource || null,
    primaryPaidProvider: paidAttribution.primaryPaidProvider || null,
    paidMediaProvider: paidAttribution.paidMediaProvider || null,
    attributionBasis: paidAttribution.attributionBasis,
    confidence: paidAttribution.confidence,
    conversionAttributionBasis: paidAttribution.conversionAttributionBasis || null,
    conversionConfidence: paidAttribution.conversionConfidence || null,
    firstPaidClickAtMs: paidAttribution.firstPaidClickAtMs || null,
    latestPaidClickAtMs: paidAttribution.latestPaidClickAtMs || null,
    acquisitionConversionDisagreement:
      Boolean(paidAttribution.acquisitionConversionDisagreement),
    acquisitionSelfReportDisagreementKind:
      paidAttribution.acquisitionSelfReportDisagreementKind || null,
    assistedProviders: paidAttribution.assistedProviders || [],
    hasGoogleClickId: Boolean(paidAttribution.hasGoogleClickId),
    hasMetaClickId: Boolean(paidAttribution.hasMetaClickId),
    clickIdTypes: paidAttribution.clickIdTypes || [],
    source:
      paidAttribution.primaryAdSource ||
      cleanValue(utm.utm_source) ||
      previous.source ||
      "unknown",
  };

  storage.setItem(STORAGE_KEY, JSON.stringify(payload));
  return payload;
}

export function buildAdAttributionPatch() {
  const attribution = readStoredAdAttribution();
  if (!attribution) return {};

  return {
    adAttribution: attribution,
  };
}
